import { SecondaryContainer as CommonContainer, Loading } from "components";
import { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { useLocale, useWindowSize } from "hooks";
import { NewPagination } from "components";
import { responsive } from "utils/theme";
import CourseCard from "components/CourseCard/CourseCard";
import translation from "./translation";
import type { GetContentParams } from "types";
import { ApiService } from "services/serverInClient";
import { toast } from "react-toastify";
import { Formatter } from "utils/formatter";
import { normalizeHomeResearchCenters } from "utils/normalizer";
import { CONTENT_TYPES } from "utils/constants";

const SIZE = 4;

const chunker = (n, targetList) => {
  const result = [];
  let clone = JSON.parse(JSON.stringify(targetList));
  if (Array.isArray(clone)) {
    (function chunk() {
      result?.push?.(clone?.splice?.(0, n));
      if (clone?.length > 0) return chunk();
    })();
    return result;
  }
  return [];
};

function TabCoursesRenderer({ tagTree }) {
  const { locale, isRTL } = useLocale();
  const [totalPagesCount, setTotalPagesCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const { width } = useWindowSize();
  const [listData, setListData] = useState([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [desktopCourses, mobileCourses] = useMemo(
    () => [chunker(4, listData), chunker(2, listData)],
    [listData]
  );

  const fetchData = async (inputParams = { page: 1 }) => {
    const page = inputParams?.page || 1;
    delete inputParams.page;
    let params: GetContentParams = {
      tagTrees: [tagTree],
      size: SIZE,
      offset: SIZE * (page - 1),
      contentTypeUniqueId: CONTENT_TYPES.RESEARCH_CARD,
      orderBy: ["content.ResearchPriority:asc"],
      ...inputParams,
    };

    try {
      setIsLoading(true);
      const apiService = new ApiService("fa");
      const response = await apiService.getContent(params, {
        formatter: false,
      });
      setTotalPagesCount(Math.ceil((response?.count || 0) / SIZE));
      const unsortedList = Formatter.formatData({ data: response });
      setListData(normalizeHomeResearchCenters(unsortedList) || []);
    } catch {
      toast.error("خطایی رخ داد");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (tagTree) {
      fetchData();
    }
  }, [tagTree]);

  const renderCommonList = (list1 = [], list2 = [], pagination = false) => {
    if (isLoading) {
      return (
        <div className="tw-flex tw-h-[300px] tw-w-full tw-justify-center tw-items-center">
          <Loading />
        </div>
      );
    }
    return (
      <>
        {(() => {
          if (width > responsive.md) {
            const listToRender = list1;
            return listToRender?.map?.((list, i) => (
              <div
                key={i}
                className={clsx(
                  "tw-mt-[50px] xxxl:tw-mt-[66.67px] tw-mx-auto tw-grid tw-gap-[31px] xxxl:tw-gap-[41.33px] lg:tw-gap-[31px]",
                  {
                    "xxxl:tw-w-[345.33px] sm:tw-w-[259px] tw-grid-cols-1":
                      list.length === 1,
                    "xxxl:tw-w-[calc(345.33px*2+41.33px)] tw-w-[calc(259px*2+31px)] tw-grid-cols-2":
                      list.length === 2,
                    "xxxl:tw-w-[calc(345.33px*3+41.33px*2)] tw-w-[calc(259px*3+31px*2)] tw-grid-cols-3":
                      list.length === 3,
                    "xxxl:tw-max-w-[calc(345.33px*4+41.33px*3)] tw-max-w-[calc(259px*4+31px*3)] tw-grid-cols-4":
                      list.length === 4,
                  }
                )}
              >
                {list?.map?.((item) => (
                  <CourseCard data={item} key={item.id} />
                ))}
              </div>
            ));
          } else {
            const listToRender = list2;
            return listToRender?.map?.((list, i) => (
              <div
                key={i}
                className={clsx(
                  "tw-mt-[50px] xxxl:tw-mt-[66.67px] tw-mx-auto tw-grid tw-gap-[31px] xxxl:tw-gap-[41.33px] lg:tw-gap-[31px]",
                  {
                    "xxxl:tw-w-[345.33px] sm:tw-w-[259px] tw-grid-cols-1":
                      list.length === 1,
                    "tw-grid-cols-1 sm:tw-grid-cols-2": list.length === 2,
                  }
                )}
              >
                {list?.map?.((item) => (
                  <CourseCard data={item} key={item.id} />
                ))}
              </div>
            ));
          }
        })()}
        {(() => {
          if (pagination) {
            return list1?.[0]?.length !== 0 ? (
              <div className="tw-mt-[30px]">
                <NewPagination
                  currentPage={currentPage}
                  totalPagesCount={totalPagesCount}
                  onPageChange={handlePageClick}
                />
              </div>
            ) : (
              <div className="tw-text-center tw-min-h-[100px] xxxl:tw-text-[17.33px] tw-mt-10">
                {translation[locale]?.NOT_FOUND}
              </div>
            );
          }
          return list1?.[0]?.length !== 0 ? (
            ""
          ) : (
            <div className="tw-text-center tw-min-h-[100px] xxxl:tw-text-[17.33px] tw-mt-10">
              {translation[locale]?.NOT_FOUND}
            </div>
          );
        })()}
      </>
    );
  };

  const onSearchClick = () => {
    let params = {};
    if (searchQuery) {
      params = {
        fieldCode: ["ResearchTitle"],
        fieldValue: [searchQuery || ""],
      };
    }
    fetchData(params);
  };

  function handlePageClick(d) {
    const selected = d?.selected + 1;
    setCurrentPage(selected);
    fetchData({ page: selected });
  }

  const handleChange = (ev) => {
    setSearchQuery(ev.target.value);
    ev.persist();
  };

  return (
    <div className="tw-pb-[30px]">
      <div>
        <CommonContainer>
          <div className="tw-flex tw-flex-col md:tw-flex-row tw-justify-between md:tw-items-center tw-w-full tw-px-4 md:tw-px-0">
            <div></div>
            <div className="tw-relative md:tw-w-[543px] tw-mt-5 md:tw-mt-0">
              <input
                type="text"
                value={searchQuery}
                className={clsx(
                  "tw-bg-[#FFFFFF] tw-text-sm xxxl:tw-text-[17.33px] tw-text-[#344456]/50 placeholder:tw-text-[#344456]/20 tw-border tw-border-[#344456]/20 focus-visible:!tw-border focus-visible:!tw-border-[#344456]/20 focus-visible:!tw-border-solid tw-font-medium tw-rounded-lg tw-py-3.5 xxxl:tw-py-[18.67px] tw-outline-none tw-min-w-[300px] xxxl:tw-min-w-[533.33px] tw-w-full",
                  {
                    "tw-text-left tw-pl-4 ltr": !isRTL,
                    "tw-pr-4": isRTL,
                  }
                )}
                placeholder={translation[locale]?.PLACEHOLDER}
                onChange={handleChange}
              />
              <button
                className={clsx(
                  "tw-absolute tw-top-1 tw-border-0 tw-cursor-pointer tw-bg-[#004883] hover:tw-bg-[#004883]/80 tw-rounded-md tw-flex tw-items-center tw-justify-center tw-p-3 xxxl:tw-p-[17px]",
                  { "tw-left-[3px]": isRTL, "tw-right-[3px]": !isRTL }
                )}
                onClick={onSearchClick}
              >
                <svg
                  width="17"
                  height="18"
                  viewBox="0 0 17 18"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M16.9225 15.9108L13.8542 12.8425C14.8817 11.5583 15.5 9.93413 15.5 8.16663C15.5 4.03079 12.135 0.666626 8 0.666626C3.865 0.666626 0.5 4.03079 0.5 8.16663C0.5 12.3025 3.865 15.6666 8 15.6666C9.76833 15.6666 11.3925 15.0483 12.6758 14.0216L15.7442 17.0891L16.9225 15.9108ZM8 14C4.78333 14 2.16667 11.3833 2.16667 8.16663C2.16667 4.95079 4.78333 2.33329 8 2.33329C11.2167 2.33329 13.8333 4.95079 13.8333 8.16663C13.8333 11.3833 11.2167 14 8 14Z"
                    fill="white"
                  />
                </svg>
              </button>
            </div>
          </div>
        </CommonContainer>
      </div>
      <CommonContainer>
        <div className="tw-mx-auto">
          <div className="tw-px-4 md:tw-px-0">
            {renderCommonList(desktopCourses, mobileCourses, true)}
          </div>
        </div>
      </CommonContainer>
    </div>
  );
}

export default TabCoursesRenderer;
